/**
 * Content validation (doc 18 §8) — "JSON content validated against schemas on
 * load with loud dev-time errors."
 *
 * Not a schema library: a schema library would be a dependency, and doc 12
 * keeps the runtime at exactly one (PixiJS). This is a set of shape checks over
 * the files the game cannot run without, expressed as the *invariants* rather
 * than as types — TypeScript already proves the types at build time, and what
 * actually goes wrong in content is a missing cross-reference, a negative cost,
 * or a shoal pointing at a species nobody wrote.
 *
 * It runs once at boot and throws with everything it found, so a content edit
 * that breaks the game says so on the first frame rather than in the fight.
 */

import abilities from './abilities.json' with { type: 'json' };
import buildings from './buildings.json' with { type: 'json' };
import enemies from './enemies.json' with { type: 'json' };
import mapD1 from './map-d1.json' with { type: 'json' };
import mapD2 from './map-d2.json' with { type: 'json' };
import mapD3 from './map-d3.json' with { type: 'json' };
import onboarding from './onboarding.json' with { type: 'json' };
import pressure from './pressure.json' with { type: 'json' };
import shoalsD1 from './shoals-d1.json' with { type: 'json' };
import shoalsD2 from './shoals-d2.json' with { type: 'json' };
import shoalsD3 from './shoals-d3.json' with { type: 'json' };
import towers from './towers.json' with { type: 'json' };
import audio from './audio.json' with { type: 'json' };

interface Named {
  name?: unknown;
}

type Bag = Record<string, unknown>;

/** Every complaint, collected — one throw with the whole list beats five. */
export function validateContent(): string[] {
  const bad: string[] = [];
  const say = (ok: boolean, why: string): void => {
    if (!ok) bad.push(why);
  };

  const species = new Set(Object.keys(enemies.species as Bag));
  const placeables = new Set([...Object.keys(towers.towers as Bag), ...Object.keys(buildings.buildings as Bag)]);

  // -- everything the player can build has a name and a non-negative price ---
  for (const [id, def] of Object.entries({ ...(towers.towers as Bag), ...(buildings.buildings as Bag) })) {
    const d = def as Bag & Named;
    say(typeof d.name === 'string' && d.name.length > 0, `${id}: no name`);
    const cost = (d.cost ?? {}) as Record<string, number>;
    for (const [res, n] of Object.entries(cost)) {
      say(typeof n === 'number' && n >= 0, `${id}: cost.${res} is ${String(n)}`);
    }
  }

  // -- every enemy is killable, and either moves or is a thing left behind --
  for (const [id, def] of Object.entries(enemies.species as Bag)) {
    const d = def as Bag;
    say(typeof d.hp === 'number' && (d.hp as number) > 0, `${id}: hp ${String(d.hp)}`);
    // speed 0 is legal and deliberate: the Mother's thread nodes are furniture
    // she leaves behind, not fish. Everything that swims has to actually swim.
    say(typeof d.speed === 'number' && (d.speed as number) >= 0, `${id}: speed ${String(d.speed)}`);
    if (d.speed === 0) say((d.dps as number) === 0, `${id}: it cannot move AND cannot be escaped`);
    say(typeof d.name === 'string' && (d.name as string).length > 0, `${id}: no name`);
    say(typeof d.codex === 'string', `${id}: nothing for the Codex to say`);
  }

  // -- every shoal points at species that exist ----------------------------
  for (const [depth, file] of [
    [1, shoalsD1],
    [2, shoalsD2],
    [3, shoalsD3],
  ] as const) {
    const all = [...(file.act1 as unknown as Bag[]), ...(file.act2 as unknown as Bag[])];
    say(all.length > 0, `d${depth}: no shoals`);
    for (const shoal of all) {
      const id = String(shoal.id);
      say(typeof shoal.name === 'string', `d${depth}/${id}: no name`);
      say(
        typeof shoal.menace === 'number' && (shoal.menace as number) >= 1 && (shoal.menace as number) <= 5,
        `d${depth}/${id}: menace ${String(shoal.menace)} is not 1..5`,
      );
      for (const g of (shoal.composition ?? []) as Bag[]) {
        say(species.has(String(g.species)), `d${depth}/${id}: no such species "${String(g.species)}"`);
        say(typeof g.count === 'number' && (g.count as number) > 0, `d${depth}/${id}: count ${String(g.count)}`);
      }
    }
  }

  // -- every map has a core on it, and its deposits are inside the grid -----
  for (const [depth, map] of [
    [1, mapD1],
    [2, mapD2],
    [3, mapD3],
  ] as const) {
    const size = map.size as number;
    say(size > 8, `d${depth}: map is ${size} tiles`);
    say(map.spawnEdges.length > 0, `d${depth}: nothing can spawn`);
    const inside = (t: number[]): boolean => t[0]! >= 0 && t[1]! >= 0 && t[0]! < size && t[1]! < size;
    say(inside([map.core.i, map.core.j]), `d${depth}: the Core is off the map`);
    for (const v of map.oreVeins) say(inside(v), `d${depth}: ore vein ${v.join(',')} is off the map`);
    for (const v of map.gasVents) say(inside(v), `d${depth}: gas vent ${v.join(',')} is off the map`);
    say(map.oreVeins.length > 0, `d${depth}: no ore anywhere`);
  }

  // -- the four abilities, and nothing but ---------------------------------
  const keys = Object.keys(abilities.abilities as Bag);
  say(keys.length === 4, `abilities: ${keys.length}, expected 4`);
  for (const k of ['q', 'w', 'e', 'r']) say(keys.includes(k), `abilities: ${k} is missing`);

  // -- Pressure: six tiers, each with copy the player can read -------------
  const tiers = pressure.tiers as Bag[];
  say(tiers.length === (pressure.maxTiers as number), `pressure: ${tiers.length} tiers, cap ${pressure.maxTiers}`);
  for (const t of tiers) {
    say(typeof t.name === 'string' && typeof t.effect === 'string', `pressure/${String(t.id)}: no copy`);
  }

  // -- onboarding: every gate a card names is a gate that exists, and every
  //    gate lets through a command about something that can actually be built
  const gates = new Set(Object.keys(onboarding.gates as Bag));
  for (const card of onboarding.cards as Bag[]) {
    say(typeof card.title === 'string' && typeof card.body === 'string', `card/${String(card.id)}: no copy`);
    if (card.gate !== null) say(gates.has(String(card.gate)), `card/${String(card.id)}: no gate "${String(card.gate)}"`);
  }
  for (const [id, gate] of Object.entries(onboarding.gates as Bag)) {
    const [kind, arg] = String((gate as Bag).allows).split(':');
    say(['place', 'pull', 'sell', 'upgrade', 'cast'].includes(String(kind)), `gate/${id}: allows "${String(kind)}"`);
    if (arg !== undefined) say(placeables.has(arg), `gate/${id}: nothing called "${arg}" can be built`);
  }

  // -- audio: nothing silent, nothing on a bus that does not exist ---------
  for (const [id, def] of Object.entries(audio.sounds as Bag)) {
    const d = def as Bag;
    say(['music', 'sfx', 'ambience'].includes(String(d.bus)), `sound/${id}: bus "${String(d.bus)}"`);
    say(Array.isArray(d.voices) && (d.voices as unknown[]).length > 0, `sound/${id}: no voices`);
  }

  return bad;
}

/**
 * Doc 18 §8: loud. In a dev build it throws — a broken content file should stop
 * the boot, not surface as a wrong number in fight nine. A production build
 * still logs, because shipping a black screen over a typo is worse.
 */
export function assertContent(dev: boolean): void {
  const bad = validateContent();
  if (bad.length === 0) return;
  const message = `CONTENT IS BROKEN (${bad.length}):\n  ${bad.join('\n  ')}`;
  if (dev) throw new Error(message);
  console.error(message);
}
